import { Component } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { ACCESS_LEVELS } from '../constants/AccessLevels';
import { defaultDisableAccessToEveryoneFormattedMessage } from '../utils/AssetAccessUtils';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AccessToggle = ({
  accessLevel,
  selectedAccessLevel,
  onChange,
  disabled,
  customMessage
}) => {
  return /*#__PURE__*/_jsx(RadioInput, {
    name: `access-toggle-${accessLevel}`,
    "data-test-id": `access-toggle-${accessLevel}`,
    checked: accessLevel === selectedAccessLevel,
    onChange: () => onChange(accessLevel),
    disabled: disabled,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: customMessage || `ui-asset-management-lib.assetAccessStates.${accessLevel}`
    })
  });
};

// Used for controlling when you can set "everyone/selected users" to also be view and edit, or view only.
const GroupedPermissionsAccessToggles = ({
  selectedAccessLevel,
  baseAccessLevel,
  editLevel,
  viewLevel,
  onChange,
  disabled
}) => {
  const groupChecked = [editLevel, viewLevel].includes(selectedAccessLevel);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(RadioInput, {
      disabled: disabled,
      checked: groupChecked,
      onChange: () => onChange(editLevel),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `ui-asset-management-lib.assetAccessStates.${baseAccessLevel}`
      })
    }), groupChecked && /*#__PURE__*/_jsxs(View, {
      marginInlineStart: "400",
      as: "div",
      children: [/*#__PURE__*/_jsx(AccessToggle, {
        disabled: disabled,
        selectedAccessLevel: selectedAccessLevel,
        accessLevel: editLevel,
        onChange: onChange
      }), /*#__PURE__*/_jsx(AccessToggle, {
        disabled: disabled,
        selectedAccessLevel: selectedAccessLevel,
        accessLevel: viewLevel,
        onChange: onChange
      })]
    })]
  });
};
class AssetAccessLevelPicker extends Component {
  // Render just the option to select available to everyone or selected users and teams
  renderBaseAccessLevels() {
    const {
      value,
      onChange,
      disabled,
      isTeamsOnly,
      isUsersOnly,
      baseAccessValue,
      useAccessLanguage,
      disableAccessToEveryone,
      DisableAccessToEveryoneFormattedMessage
    } = this.props;
    return /*#__PURE__*/_jsxs(ToggleGroup, {
      name: "access-level",
      children: [/*#__PURE__*/_jsx(Tooltip, {
        placement: "left",
        disabled: !disableAccessToEveryone,
        title: DisableAccessToEveryoneFormattedMessage && /*#__PURE__*/_jsx(DisableAccessToEveryoneFormattedMessage, {}),
        children: /*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx(AccessToggle, {
            selectedAccessLevel: value,
            accessLevel: ACCESS_LEVELS.EVERYONE,
            onChange: onChange,
            disabled: Boolean(disabled || disableAccessToEveryone)
          })
        })
      }), /*#__PURE__*/_jsx(AccessToggle, {
        selectedAccessLevel: value,
        accessLevel: ACCESS_LEVELS.SELECTED_USERS_AND_TEAMS,
        onChange: onChange,
        disabled: disabled,
        customMessage: baseAccessValue && `ui-asset-management-lib.assetAccessStates.${baseAccessValue}` || isTeamsOnly && 'ui-asset-management-lib.assetAccessStates.SELECTED_TEAMS' || isUsersOnly && 'ui-asset-management-lib.assetAccessStates.SELECTED_USERS' || useAccessLanguage && 'ui-asset-management-lib.assetAccessStates.SELECTED_USERS_AND_TEAMS_ACCESS'
      })]
    });
  }

  // Render base options with an additional select option to make it private
  renderAccessLevelsWithPrivate() {
    const {
      value,
      onChange,
      disabled,
      disableAccessToEveryone,
      DisableAccessToEveryoneFormattedMessage,
      baseAccessValue,
      isTeamsOnly,
      isUsersOnly,
      useAccessLanguage
    } = this.props;
    return /*#__PURE__*/_jsxs(ToggleGroup, {
      name: "access-level",
      children: [/*#__PURE__*/_jsx(AccessToggle, {
        selectedAccessLevel: value,
        accessLevel: ACCESS_LEVELS.PRIVATE,
        onChange: onChange,
        disabled: disabled
      }), /*#__PURE__*/_jsx(Tooltip, {
        placement: "left",
        disabled: !disableAccessToEveryone,
        title: DisableAccessToEveryoneFormattedMessage && /*#__PURE__*/_jsx(DisableAccessToEveryoneFormattedMessage, {}),
        children: /*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx(AccessToggle, {
            selectedAccessLevel: value,
            accessLevel: ACCESS_LEVELS.EVERYONE,
            onChange: onChange,
            disabled: Boolean(disabled || disableAccessToEveryone)
          })
        })
      }), /*#__PURE__*/_jsx(AccessToggle, {
        selectedAccessLevel: value,
        accessLevel: ACCESS_LEVELS.SELECTED_USERS_AND_TEAMS,
        onChange: onChange,
        disabled: disabled,
        customMessage: baseAccessValue && `ui-asset-management-lib.assetAccessStates.${baseAccessValue}` || isTeamsOnly && 'ui-asset-management-lib.assetAccessStates.SELECTED_TEAMS' || isUsersOnly && 'ui-asset-management-lib.assetAccessStates.SELECTED_USERS' || useAccessLanguage && 'ui-asset-management-lib.assetAccessStates.SELECTED_USERS_AND_TEAMS_ACCESS'
      })]
    });
  }

  // Render the full list of options with the ability to set it as view only or view and edit for all users option
  renderAccessLevelsWithViewPermissions() {
    const {
      value,
      onChange,
      disabled,
      disableAccessToEveryone,
      DisableAccessToEveryoneFormattedMessage
    } = this.props;
    return /*#__PURE__*/_jsxs(ToggleGroup, {
      name: "access-level",
      children: [/*#__PURE__*/_jsx(AccessToggle, {
        selectedAccessLevel: value,
        accessLevel: ACCESS_LEVELS.PRIVATE,
        onChange: onChange,
        disabled: disabled
      }), /*#__PURE__*/_jsx(Tooltip, {
        placement: "left",
        disabled: !disableAccessToEveryone,
        title: DisableAccessToEveryoneFormattedMessage && /*#__PURE__*/_jsx(DisableAccessToEveryoneFormattedMessage, {}),
        children: /*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx(GroupedPermissionsAccessToggles, {
            selectedAccessLevel: value,
            baseAccessLevel: ACCESS_LEVELS.EVERYONE,
            editLevel: ACCESS_LEVELS.EVERYONE_VIEW_AND_EDIT,
            viewLevel: ACCESS_LEVELS.EVERYONE_VIEW_ONLY,
            onChange: onChange,
            disabled: Boolean(disabled || disableAccessToEveryone)
          })
        })
      }), /*#__PURE__*/_jsx(GroupedPermissionsAccessToggles, {
        selectedAccessLevel: value,
        baseAccessLevel: ACCESS_LEVELS.SELECTED_USERS_AND_TEAMS,
        editLevel: ACCESS_LEVELS.SELECTED_USERS_AND_TEAMS_VIEW_AND_EDIT,
        viewLevel: ACCESS_LEVELS.SELECTED_USERS_AND_TEAMS_VIEW_ONLY,
        onChange: onChange,
        disabled: disabled
      })]
    });
  }
  render() {
    const {
      allowPrivate,
      allowSetEditLevel
    } = this.props;
    if (allowSetEditLevel) {
      return this.renderAccessLevelsWithViewPermissions();
    }
    if (allowPrivate) {
      return this.renderAccessLevelsWithPrivate();
    }
    return this.renderBaseAccessLevels();
  }
}
AssetAccessLevelPicker.defaultProps = {
  DisableAccessToEveryoneFormattedMessage: defaultDisableAccessToEveryoneFormattedMessage
};
export default AssetAccessLevelPicker;